import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export const commentKey = new PluginKey("lf-comments");

export function flatten(doc) {
  let text = "";
  const pos = [];
  doc.descendants((node, p) => {
    if (node.isText) {
      for (let i = 0; i < node.text.length; i++) { text += node.text[i]; pos.push(p + i); }
    } else if (node.isBlock && text.length && text[text.length - 1] !== "\n") {
      text += "\n"; pos.push(-1);
    }
  });
  return { text, pos };
}

const score = (a, b) => { let n = 0; while (n < a.length && n < b.length && a[n] === b[n]) n++; return n; };

export function findRange(flat, c) {
  const { text, pos } = flat;
  if (!c.quote) {
    const k = text.indexOf(c.prefix + c.suffix);
    const at = k === -1 ? -1 : k + c.prefix.length;
    if (k === -1) return null;
    const p = at > 0 ? pos[at - 1] + 1 : pos[at];
    return p >= 0 ? { from: p, to: p } : null;
  }
  let best = null, bestScore = -1, i = text.indexOf(c.quote);
  while (i !== -1) {
    const pre = text.slice(Math.max(0, i - c.prefix.length), i);
    const s = score([...pre].reverse().join(""), [...c.prefix].reverse().join("")) + score(text.slice(i + c.quote.length), c.suffix);
    if (s > bestScore) { bestScore = s; best = i; }
    i = text.indexOf(c.quote, i + 1);
  }
  if (best === null) return null;
  const from = pos[best], last = pos[best + c.quote.length - 1];
  return from >= 0 && last >= 0 ? { from, to: last + 1 } : null;
}

export function anchorFromSelection(state) {
  const { from, to } = state.selection;
  const flat = flatten(state.doc);
  if (to <= from) {
    let at = flat.pos.findIndex((p) => p >= from);
    if (at === -1) at = flat.text.length;
    return { quote: "", prefix: flat.text.slice(Math.max(0, at - 40), at), suffix: flat.text.slice(at, at + 40) };
  }
  const start = flat.pos.findIndex((p) => p >= from);
  let end = -1;
  for (let k = flat.pos.length - 1; k >= 0; k--) if (flat.pos[k] >= 0 && flat.pos[k] < to) { end = k; break; }
  if (start < 0 || end < start) return null;
  const quote = flat.text.slice(start, end + 1).trim();
  if (!quote) return null;
  const s = flat.text.indexOf(quote, start);
  return { quote, prefix: flat.text.slice(Math.max(0, s - 40), s), suffix: flat.text.slice(s + quote.length, s + quote.length + 40) };
}

const build = (doc, comments, active) => {
  const flat = flatten(doc);
  const decos = [];
  comments.forEach((c) => {
    const r = findRange(flat, c);
    if (!r) return;
    const on = c.id === active ? " lf-comment-active" : "";
    if (c.kind === "suggestion") {
      if (r.to > r.from) decos.push(Decoration.inline(r.from, r.to, { class: `lf-sugg-del${on}`, "data-comment-id": c.id }));
      if (c.replacement) {
        decos.push(Decoration.widget(r.to, () => {
          const el = document.createElement("span");
          el.className = `lf-sugg-ins${on}`;
          el.setAttribute("data-comment-id", c.id);
          el.textContent = c.replacement;
          return el;
        }, { key: `s-${c.id}-${c.replacement}-${on}`, side: 1 }));
      }
    } else if (r.to > r.from) decos.push(Decoration.inline(r.from, r.to, { class: `lf-comment-hl${on}`, "data-comment-id": c.id }));
  });
  return DecorationSet.create(doc, decos);
};

export const CommentHighlight = Extension.create({
  name: "commentHighlight",
  addOptions() { return { onPick: () => {} }; },
  addProseMirrorPlugins() {
    const { onPick } = this.options;
    return [new Plugin({
      key: commentKey,
      state: {
        init: () => ({ comments: [], active: null, decos: DecorationSet.empty }),
        apply(tr, prev, _old, state) {
          const meta = tr.getMeta(commentKey);
          const next = meta ? { ...prev, ...meta } : prev;
          if (meta || tr.docChanged) return { ...next, decos: build(state.doc, next.comments, next.active) };
          return next;
        },
      },
      props: {
        decorations: (state) => commentKey.getState(state).decos,
        handleClick: (view, pos, event) => {
          const id = event.target?.closest?.("[data-comment-id]")?.getAttribute("data-comment-id");
          if (id) onPick(id);
          return false;
        },
      },
    })];
  },
});

export const setComments = (editor, comments, active) => {
  if (editor && !editor.isDestroyed) editor.view.dispatch(editor.state.tr.setMeta(commentKey, { comments, active }));
};
